import { z } from 'zod/v3';
import { Parser } from '@cotera/era';

export { providerForToolKey } from './provider-for-tool-key';

// ============================================================================
// Credential Source Constants
// ============================================================================

/**
 * Credential source types - how credentials are provided for a tool.
 * These values match the strategy names returned by the API.
 */
export const CredentialSource = {
  WORKOS_PIPE: 'workos_pipe',
  PLATFORM_MANAGED: 'platform_managed',
  OAUTH_FLOW: 'oauth_flow',
  USER_PROVIDED: 'user_provided',
  FALLBACK: 'fallback',
  NONE: 'none',
} as const;

type CredentialSourceType =
  (typeof CredentialSource)[keyof typeof CredentialSource];

/**
 * Strategies that are platform-managed (built-in) or require no user auth.
 * Tools with only these strategies don't need user credential configuration.
 */
export const NO_AUTH_STRATEGIES: readonly CredentialSourceType[] = [
  CredentialSource.PLATFORM_MANAGED,
  CredentialSource.NONE,
];

/**
 * Check if a strategy name is a no-auth strategy.
 */
export function isNoAuthStrategy(strategyName: string): boolean {
  return (NO_AUTH_STRATEGIES as readonly string[]).includes(strategyName);
}

// ============================================================================
// Schema Definitions
// ============================================================================

const implementationTypeSchema = z.enum([
  'builtin',
  'agent',
  'workflow',
  'custom_js',
  'legacy',
]);

const strategyDescriptorSchema = z.object({
  name: z.string(),
  fieldNames: z.array(z.string()).optional(),
  displayLabel: z.string().optional(),
});

/**
 * Schema for SerializableToolDefinition - the result of ToolDefinition.serialize()
 */
const toolListItemResponse = z.object({
  key: z.string(),
  /** The implementation key (e.g., "googlesearch/web/search") - stable identifier */
  implementationKey: z.string(),
  name: z.string(),
  description: z.string(),
  defaultDescription: z.string().optional(),
  provider: z.string(),
  providerDisplayName: z.string(),
  providerDescription: z.string().optional(),
  implementationType: implementationTypeSchema,
  /** Whether the tool is read-only (query) or has side effects (mutation). */
  toolType: z.enum(['query', 'mutation', 'interruptable']).optional(),
  /** When true, the tool receives streamed input during LLM generation. */
  streamable: z.boolean().optional(),
  /**
   * Every context the tool can run in. Filter on this so a tool is only
   * offered where it will actually work — a chat-only tool dropped into a
   * workflow ToolCall node throws at run time.
   */
  availableContexts: z
    .array(z.enum(['standalone', 'conversation', 'workflow']))
    .optional(),
  credentialStrategyDescriptors: z.array(strategyDescriptorSchema),
  credentialCacheTTL: z.number().optional(),
  inputTy: Parser.ExtendedAttributeTypeSchema,
  /** Per-field descriptions for top-level input params (dropped from `inputTy`). */
  inputFieldDescriptions: z.record(z.string(), z.string()).optional(),
  outputTy: Parser.ExtendedAttributeTypeSchema,
  requiredResources: z.record(z.string(), z.number()),
  canExecute: z.boolean(),
  notExecutableReason: z.string().optional(),
  hidden: z.boolean().optional(),
  workflowStepCount: z.number().optional(),
});

export type ToolListItemResponse = z.infer<typeof toolListItemResponse>;

/**
 * Schema for credential field definition
 */
const credentialFieldSchema = z.object({
  name: z.string(),
  label: z.string(),
  type: z.enum(['text', 'password', 'textarea']),
  required: z.boolean(),
  placeholder: z.string().optional(),
  /** Optional guidance rendered under the field (e.g. host format, sandbox form). */
  helpText: z.string().optional(),
});

export type CredentialField = z.infer<typeof credentialFieldSchema>;

const strategyCustomSchema = z.object({
  name: z.string(),
  t: z.literal('custom'),
});

const strategyWorkosSchema = z.object({
  name: z.string(),
  t: z.literal('workos'),
  provider: z.string(),
});

/**
 * Discriminated credential strategy shape for tool credential info.
 * `name` matches the tool's strategy `name` for createProviderAccount.
 */
const strategySchema = z.discriminatedUnion('t', [
  strategyCustomSchema,
  strategyWorkosSchema,
]);

export type Strategy = z.infer<typeof strategySchema>;

/**
 * Schema for a credential strategy available for a tool
 */
const credentialStrategyInfoSchema = z.object({
  displayName: z.string(),
  description: z.string(),
  credentialFields: z.array(credentialFieldSchema),
  strategy: strategySchema,
});

export type CredentialStrategyInfo = z.infer<
  typeof credentialStrategyInfoSchema
>;

/**
 * WorkOS / Slack OAuth connect or app install (not a manual credential form).
 */
export function isCredentialStrategyOauthConnect(strategy: Strategy): boolean {
  if (strategy.t === 'workos') {
    return strategy.provider.length > 0;
  }
  return (
    strategy.t === 'custom' &&
    (strategy.name === 'slack_oauth' || strategy.name === 'slack_auth')
  );
}

/**
 * Provider slug for WorkOS OAuth connect UI, or Slack when using custom Slack OAuth strategy.
 */
export function getCredentialStrategyOauthProvider(
  strategy: Strategy
): string | undefined {
  if (strategy.t === 'workos') {
    return strategy.provider.length > 0 ? strategy.provider : undefined;
  }
  if (
    strategy.t === 'custom' &&
    (strategy.name === 'slack_oauth' || strategy.name === 'slack_auth')
  ) {
    return 'slack';
  }
  return undefined;
}

/**
 * Schema for tool credential info response
 */
const toolCredentialInfoResponse = z.object({
  toolKey: z.string(),
  provider: z.string(),
  displayName: z.string(),
  strategies: z.array(credentialStrategyInfoSchema),
});

export type ToolCredentialInfoResponse = z.infer<
  typeof toolCredentialInfoResponse
>;

/**
 * Schema for provider account creation
 */
const createProviderAccountInput = z.object({
  toolKey: z.string(),
  strategyName: z.string(),
  displayName: z.string().optional(),
  credentials: z.record(z.string(), z.string()),
});

type CreateProviderAccountInput = z.infer<typeof createProviderAccountInput>;

const providerAccountResponse = z.object({
  id: z.string(),
  provider: z.string(),
  displayName: z.string(),
});

type ProviderAccountResponse = z.infer<typeof providerAccountResponse>;

/**
 * Schema for WorkOS data provider list items
 */
const workosDataProviderSchema = z.object({
  provider: z.string(),
  status: z.enum(['connected', 'not_connected']),
});

export type WorkOSDataProvider = z.infer<typeof workosDataProviderSchema>;

/**
 * Schema for WorkOS authorization URL response
 */
const workosAuthorizationUrlResponse = z.object({
  authorizationUrl: z.string().optional(),
  alreadyConnected: z.boolean().optional(),
});

type WorkOSAuthorizationUrlResponse = z.infer<
  typeof workosAuthorizationUrlResponse
>;

/**
 * Schema for WorkOS disconnect response
 */
const workosDisconnectResponse = z.object({
  success: z.boolean(),
});

type WorkOSDisconnectResponse = z.infer<typeof workosDisconnectResponse>;

const syncWorkosProviderAccountResponse = z.object({
  providerKeys: z.array(z.string()),
  created: z.number(),
  updated: z.number(),
  skipped: z.number(),
});

type SyncWorkosProviderAccountResponse = z.infer<
  typeof syncWorkosProviderAccountResponse
>;

/**
 * Schema for provider account list item
 */
const providerAccountListItem = z.object({
  id: z.string(),
  displayName: z.string(),
  status: z.enum(['active', 'default', 'disabled', 'archived']),
  isBuiltIn: z.boolean(),
  createdAt: z.string(),
});

export type ProviderAccountListItem = z.infer<typeof providerAccountListItem>;

/**
 * Schema for list provider accounts response
 */
const listProviderAccountsResponse = z.object({
  provider: z.string(),
  accounts: z.array(providerAccountListItem),
});

type ListProviderAccountsResponse = z.infer<
  typeof listProviderAccountsResponse
>;

/**
 * Schema for a provider with its accounts
 */
const providerWithAccounts = z.object({
  key: z.string(),
  displayName: z.string(),
  accounts: z.array(providerAccountListItem),
});

export type ProviderWithAccounts = z.infer<typeof providerWithAccounts>;

/**
 * Schema for list all providers response
 */
const listAllProvidersResponse = z.object({
  providers: z.array(providerWithAccounts),
});

type ListAllProvidersResponse = z.infer<typeof listAllProvidersResponse>;

const slackBotInstallationResponse = z.object({
  installed: z.boolean(),
  teamId: z.string().optional(),
});

export type SlackBotInstallationResponse = z.infer<
  typeof slackBotInstallationResponse
>;

const linearBotInstallationResponse = z.object({
  installed: z.boolean(),
  organizationId: z.string().optional(),
});

export type LinearBotInstallationResponse = z.infer<
  typeof linearBotInstallationResponse
>;

const linearTeamAgentConfigSchema = z.object({
  teamId: z.string(),
  teamName: z.string(),
  agentId: z.string().nullable(),
});

export type LinearTeamAgentConfig = z.infer<typeof linearTeamAgentConfigSchema>;

const linearTeamAgentsResponse = z.object({
  teams: z.array(linearTeamAgentConfigSchema),
});

export type LinearTeamAgentsResponse = z.infer<typeof linearTeamAgentsResponse>;

const slackChannelAgentConfigSchema = z.object({
  channelId: z.string(),
  channelName: z.string(),
  agentId: z.string().nullable(),
});

export type SlackChannelAgentConfig = z.infer<
  typeof slackChannelAgentConfigSchema
>;

const slackChannelAgentsResponse = z.object({
  channels: z.array(slackChannelAgentConfigSchema),
});

export type SlackChannelAgentsResponse = z.infer<
  typeof slackChannelAgentsResponse
>;

const teamsChannelAgentConfigSchema = z.object({
  channelId: z.string(),
  channelName: z.string(),
  agentId: z.string().nullable(),
});

export type TeamsChannelAgentConfig = z.infer<
  typeof teamsChannelAgentConfigSchema
>;

const teamsChannelAgentsResponse = z.object({
  channels: z.array(teamsChannelAgentConfigSchema),
});

export type TeamsChannelAgentsResponse = z.infer<
  typeof teamsChannelAgentsResponse
>;
